/**
 * Reading and writing a medication dose — the one place the form, the lists and
 * the daily log ask.
 *
 * The dose used to go through parseFloat, which reads as far as it understands
 * and silently drops the rest: "1/4" tablet saved as 1 tablet, and "2,5" mg as
 * 2 mg. A quarter tablet is the ordinary dose of many cat medications, and owners
 * in most of Europe write a decimal comma, so both were common and both saved a
 * different dose from the one on the label. Wrong doses then reached the vet
 * sheet and the CSV.
 *
 * So parsing is strict: a number (decimal point or decimal comma), a half or a
 * quarter ("1/2", "½", "3/4"), or a whole number plus one of those ("1 1/2",
 * "1½"). Anything else is refused with a message rather than guessed at.
 * Halves and quarters are exact in the backend's Numeric(10, 3) column, so the
 * stored value is the dose exactly as written; thirds are refused for that reason.
 */

import i18n from '../../i18n';
import { formatNumber } from '../../lib/format';

export type DoseParse =
  | { ok: true; value: number | null }
  | { ok: false; message: string };


/** The fractions a tablet can be split into, in both the typed and the glyph form. */
const FRACTIONS: Record<string, number> = {
  '1/4': 0.25,
  '1/2': 0.5,
  '2/4': 0.5,
  '3/4': 0.75,
  '¼': 0.25,
  '½': 0.5,
  '¾': 0.75,
};

/** Plain number: digits with at most one decimal point OR comma. */
const NUMBER_RE = /^(\d+(?:[.,]\d+)?|[.,]\d+)$/;
/** Optional whole part, then a fraction — "1 1/2", "1½", "¼". A typed fraction
 *  needs a space after the whole number: "11/2" is refused, not read as 1½. */
const FRACTION_RE = /^(?:(\d+) )?(\d\/\d)$|^(?:(\d+) ?)?([¼½¾])$/;

/**
 * Parse what the owner typed. An empty field is a valid "no dose recorded"
 * (null) — the dose is optional on the form.
 */
export function parseDose(raw: string): DoseParse {
  const text = raw.trim().replace(/\s+/g, ' ');
  if (text === '') return { ok: true, value: null };

  if (NUMBER_RE.test(text)) {
    return { ok: true, value: Number(text.replace(',', '.')) };
  }

  const match = FRACTION_RE.exec(text);
  if (match) {
    const fraction = FRACTIONS[match[2] ?? match[4]];
    if (fraction != null) {
      const wholeText = match[1] ?? match[3];
      const whole = wholeText != null ? Number(wholeText) : 0;
      return { ok: true, value: whole + fraction };
    }
  }

  return { ok: false, message: i18n.t('medications.dose.invalid') };
}

/** A dose and its unit as shown, singular ("one") or plural ("many"). Not i18next
 *  plurals: the rule here is "up to and including one is singular", so ½ tablet. */
type UnitForms = { one: (amount: string) => string; many: (amount: string) => string };

const TABLET: UnitForms = {
  one: (amount) => i18n.t('medications.dose.tabletOne', { amount }),
  many: (amount) => i18n.t('medications.dose.tabletMany', { amount }),
};
const CAPSULE: UnitForms = {
  one: (amount) => i18n.t('medications.dose.capsuleOne', { amount }),
  many: (amount) => i18n.t('medications.dose.capsuleMany', { amount }),
};
const DROP: UnitForms = {
  one: (amount) => i18n.t('medications.dose.dropOne', { amount }),
  many: (amount) => i18n.t('medications.dose.dropMany', { amount }),
};
const UNIT: UnitForms = {
  one: (amount) => i18n.t('medications.dose.unitOne', { amount }),
  many: (amount) => i18n.t('medications.dose.unitMany', { amount }),
};

/** Units counted as whole objects, which an owner splits into fractions. */
const COUNTED_UNITS: Record<string, UnitForms> = {
  tablets: TABLET,
  tablet: TABLET,
  capsules: CAPSULE,
  capsule: CAPSULE,
};

/** Other units with a singular/plural form. mg, mcg and mL do not change. */
const PLURAL_UNITS: Record<string, UnitForms> = {
  drops: DROP,
  drop: DROP,
  units: UNIT,
  unit: UNIT,
};

/** How a unit code is shown when it is not simply written as stored. */
function unitLabel(key: string): string | undefined {
  return key === 'ml' ? i18n.t('medications.units.ml') : undefined;
}

const GLYPHS: Record<number, string> = { 0.25: '¼', 0.5: '½', 0.75: '¾' };

/** "1½", "¼", "2" — or null when the value is not a whole number of quarters. */
function asFraction(value: number): string | null {
  const whole = Math.floor(value);
  const part = Number((value - whole).toFixed(3));
  if (part === 0) return formatNumber(whole);
  const glyph = GLYPHS[part];
  if (!glyph) return null;
  return whole === 0 ? glyph : `${formatNumber(whole)}${glyph}`;
}

/** Trim a stored Numeric(10,3) to how it was written: 2.500 → "2.5". */
function asDecimal(value: number): string {
  return String(Number(value.toFixed(3)));
}

/** The same, for display. */
function asDisplayDecimal(value: number): string {
  return formatNumber(Number(value.toFixed(3)));
}

/**
 * A dose as the owner would say it: "¼ tablet", "1½ tablets", "2.5 mg",
 * "1 drop", "0.5 mL". Anything up to and including one is singular.
 */
export function formatDose(dose: number | string | null | undefined, unit: string | null | undefined): string {
  if (dose == null || dose === '') return '';
  const value = typeof dose === 'number' ? dose : Number(dose);
  if (!Number.isFinite(value)) return '';
  const key = (unit || 'mg').toLowerCase();

  const counted = COUNTED_UNITS[key];
  if (counted) {
    const amount = asFraction(value) ?? asDisplayDecimal(value);
    return value <= 1 ? counted.one(amount) : counted.many(amount);
  }

  const plural = PLURAL_UNITS[key];
  if (plural) {
    const amount = asDisplayDecimal(value);
    return value <= 1 ? plural.one(amount) : plural.many(amount);
  }

  return i18n.t('medications.dose.amountWithUnit', {
    amount: asDisplayDecimal(value),
    unit: unitLabel(key) ?? unit ?? i18n.t('medications.units.mg'),
  });
}

/**
 * The dose as it goes back into the edit field. Tablets and capsules come back
 * in the typed fraction form ("1 1/2"), which parseDose reads, so an untouched
 * edit saves the same value.
 */
export function doseForInput(dose: number | null | undefined, unit: string | null | undefined): string {
  if (dose == null) return '';
  const value = Number(dose);
  if (!Number.isFinite(value)) return '';
  if (COUNTED_UNITS[(unit || '').toLowerCase()]) {
    const whole = Math.floor(value);
    const part = Number((value - whole).toFixed(3));
    const typed: Record<number, string> = { 0.25: '1/4', 0.5: '1/2', 0.75: '3/4' };
    if (part === 0) return String(whole);
    if (typed[part]) return whole === 0 ? typed[part] : `${whole} ${typed[part]}`;
  }
  return asDecimal(value);
}
